import { ReactNode, CSSProperties, KeyboardEvent, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { CloseIcon } from '@/components/base/Icons/RocheIconPack';
import IconButton from '@/storybook/components/IconButton/IconButton';
import Text from '@/storybook/components/Text/Text';
import ScrollArea from '@/storybook/components/ScrollArea/ScrollArea';
import styles from './Modal.module.css';

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const MODAL_VARIANT_DARK = 'dark';
const MODAL_VARIANT_LIGHT = 'light';

type ModalVariant = typeof MODAL_VARIANT_DARK | typeof MODAL_VARIANT_LIGHT;

export type ModalProps = {
  'isOpen': boolean;
  'onClose': VoidFunction;
  'title': string;
  'children': ReactNode;
  'className'?: string;
  'modalContentClassName'?: string;
  'scrollAreaRootClassName'?: string;
  'backdropClassName'?: string;
  'style'?: CSSProperties;
  'preventBackdropClose'?: boolean;
  'disablePortal'?: boolean;
  'data-testid'?: string;
  'keepMounted'?: boolean;
  'variant'?: ModalVariant;
};

export default function Modal({
  isOpen,
  onClose,
  title,
  children,
  className,
  style,
  modalContentClassName = '',
  scrollAreaRootClassName = '',
  backdropClassName = '',
  preventBackdropClose = false,
  disablePortal = false,
  'data-testid': dataTestId = 'modal',
  keepMounted = false,
  variant = MODAL_VARIANT_DARK,
}: ModalProps) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const previouslyFocused = document.activeElement;
    const container = containerRef.current;
    const firstFocusable = container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
    (firstFocusable ?? container)?.focus();

    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen]);

  const handleBackdropClick = () => {
    if (preventBackdropClose) return;
    onClose();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (preventBackdropClose) return;
      onClose();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = containerRef.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    if (!focusable || focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;

    if (event.shiftKey && active === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  };

  if (!isOpen && !keepMounted) return null;

  const isLight = variant === MODAL_VARIANT_LIGHT;

  const modal = (
    <>
      <section
        className={cx(styles.backdrop, backdropClassName)}
        onClick={handleBackdropClick}
        style={{ display: !isOpen ? 'none' : undefined }}
      />
      <div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cx(styles.modalContainer, isLight ? styles.modalContainerLight : null, className)}
        style={{ ...style, display: !isOpen ? 'none' : undefined }}
        data-testid={dataTestId}
      >
        <section className={styles.modalHeader}>
          <Text as="h3" id={titleId} className={styles.title}>
            {title}
          </Text>
          <IconButton
            appearance="text"
            theme={isLight ? 'light' : 'dark'}
            size="M"
            onClick={onClose}
            icon={<CloseIcon />}
            aria-label={t('components.modal.closeAriaLabel')}
            className={isLight ? styles.closeButtonLight : undefined}
          />
        </section>
        <ScrollArea
          scrollbars="y"
          as="section"
          className={cx(styles.modalContent, scrollAreaRootClassName)}
          classNames={{ viewport: cx(styles.modalContentViewport, modalContentClassName) }}
        >
          {children}
        </ScrollArea>
      </div>
    </>
  );

  return disablePortal ? modal : createPortal(modal, document.body);
}
